import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { fetchQuarantine } from '../lib/queries.ts';
import type { AuditEvent } from '../lib/types.ts';

interface AuditEventMetadata {
  label?: string;
  context?: Record<string, unknown>;
  diff?: Record<string, [unknown, unknown]>;
  snapshot?: Record<string, unknown>;
}

interface EntityRoute {
  base: string;
  hasDetail: boolean;
}

/** entityType -> route. Types without a detail page link to the list route instead. */
const ENTITY_ROUTES: Record<string, EntityRoute> = {
  film: { base: '/films', hasDetail: true },
  clip: { base: '/clips', hasDetail: false },
  campaign: { base: '/campaigns', hasDetail: true },
  post_draft: { base: '/queue', hasDetail: false },
  account: { base: '/accounts', hasDetail: false },
  user: { base: '/users', hasDetail: false },
};

/** Entity types with no standalone detail page of their own — they're always
 *  viewed in the context of their parent film, so prefer linking there when
 *  the audit event's context carries a filmId. */
const FILM_DESCENDANT_TYPES = new Set(['clip', 'clip_variant', 'moment']);

function renderValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function KvTable({ data }: { data: Record<string, unknown> }): React.ReactElement {
  const { t } = useTranslation();
  const entries = Object.entries(data);
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--line)', fontSize: 13, color: 'var(--text-2)', width: '42%' }}>
              {t(`audit.fields.${k}`, { defaultValue: k })}
            </td>
            <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--line)', fontSize: 13 }}>{renderValue(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AuditEventDetail({ event, onClose }: { event: AuditEvent; onClose: () => void }): React.ReactElement {
  const { t } = useTranslation();
  const metadata = event.metadata as AuditEventMetadata | null;

  const [quarantineIds, setQuarantineIds] = useState<Set<string> | null>(null);

  const route = event.entityType ? ENTITY_ROUTES[event.entityType] : undefined;
  // Quarantinability is independent of ENTITY_ROUTES — some quarantinable
  // types (post, moment, clip_variant) have no detail/list route.
  const canCheckQuarantine = !!(event.entityType && event.entityId);

  useEffect(() => {
    let cancelled = false;
    if (!canCheckQuarantine) {
      setQuarantineIds(new Set());
      return;
    }
    fetchQuarantine()
      .then((res) => {
        if (cancelled) return;
        setQuarantineIds(new Set(res.items.map((i) => `${i.type}:${i.id}`)));
      })
      .catch(() => {
        // Degrade to the live-card link — never leave the panel broken.
        if (!cancelled) setQuarantineIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [canCheckQuarantine, event.entityType, event.entityId]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const inQuarantine = !!(event.entityType && event.entityId && quarantineIds?.has(`${event.entityType}:${event.entityId}`));
  const isPurged = event.action === 'purged';
  const quarantineResolved = quarantineIds != null;

  const hasContext = !!(metadata?.context && Object.keys(metadata.context).length > 0);
  const hasDiff = !!(metadata?.diff && Object.keys(metadata.diff).length > 0);
  const hasSnapshot = !!(metadata?.snapshot && Object.keys(metadata.snapshot).length > 0);

  const btnStyle: React.CSSProperties = {
    display: 'inline-block',
    padding: '8px 12px',
    borderRadius: 8,
    background: 'var(--neon)',
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 600,
    textAlign: 'center',
  };
  const ghostBtnStyle: React.CSSProperties = { ...btnStyle, background: 'var(--neon-soft)', color: 'var(--neon)' };
  const noteStyle: React.CSSProperties = {
    fontSize: 12.5,
    color: 'var(--text-2)',
    background: 'var(--bg-soft)',
    padding: '8px 10px',
    borderRadius: 8,
  };
  const warnNoteStyle: React.CSSProperties = { ...noteStyle, color: 'var(--amber, #B45309)', background: 'rgba(180,83,9,.10)' };

  // Resolution order: (1) currently quarantined — regardless of whether
  // entityType has a detail route — always wins; (2) permanently purged;
  // (3) a live entity with a known route; (4) nothing to show.
  const renderEntityAction = (): React.ReactElement | null => {
    if (!quarantineResolved) return null;

    if (inQuarantine) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6 }}>
          <Link to="/audit?tab=quarantine" onClick={onClose} style={ghostBtnStyle}>
            {t('audit.detail.viewInQuarantine')} →
          </Link>
        </div>
      );
    }

    if (isPurged) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6 }}>
          <div style={warnNoteStyle}>{t('audit.detail.permanentlyDeleted')}</div>
        </div>
      );
    }

    const isFilmDescendant = !!(event.entityType && FILM_DESCENDANT_TYPES.has(event.entityType));

    if (route && event.entityId && (route.hasDetail || !isFilmDescendant)) {
      const to = route.hasDetail ? `${route.base}/${event.entityId}` : route.base;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6 }}>
          <Link to={to} onClick={onClose} style={btnStyle}>
            {t('audit.detail.openCard')} →
          </Link>
        </div>
      );
    }

    // Clips/variants/moments have no useful standalone detail page — link to
    // the parent film instead, when the audit event's context carries one.
    const filmId = metadata?.context?.filmId;
    if (isFilmDescendant && typeof filmId === 'string' && filmId.length > 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6 }}>
          <Link to={`/films/${filmId}`} onClick={onClose} style={btnStyle}>
            {t('audit.detail.openFilm')} →
          </Link>
        </div>
      );
    }

    return null;
  };

  const entityTypeLabel = event.entityType
    ? t(`audit.entityTypes.${event.entityType}`, { defaultValue: event.entityType })
    : '—';

  const techLine = `${[event.method, event.path].filter(Boolean).join(' ') || '—'} · ${event.entityType ?? '—'}${event.entityId ? `:${event.entityId}` : ''}`;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,.28)', zIndex: 40,
        }}
      />
      <aside
        style={{
          position: 'fixed', top: 0, right: 0, height: '100vh', width: 420, maxWidth: '92vw',
          background: 'var(--bg-card)', borderLeft: '1px solid var(--line)',
          boxShadow: '-8px 0 30px rgba(15,23,42,.12)', zIndex: 41,
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <button
            type="button"
            onClick={onClose}
            title={t('audit.detail.close')}
            style={{
              float: 'right', border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-3)', lineHeight: 1,
            }}
          >
            ×
          </button>
          <h2 style={{ margin: '0 0 4px', fontSize: 16 }}>{event.action}</h2>
          <div style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {event.actorEmail ?? '—'} · {new Date(event.createdAt).toLocaleString()} · {event.statusCode ?? '—'} · {entityTypeLabel}
          </div>
        </div>

        <div style={{ padding: '16px 18px', overflow: 'auto', flex: 1 }}>
          {renderEntityAction()}

          {hasSnapshot && (
            <>
              <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-3)', margin: '18px 0 8px' }}>
                {t('audit.detail.whatDeleted')}
              </h3>
              <KvTable data={metadata!.snapshot!} />
            </>
          )}

          {hasContext && (
            <>
              <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-3)', margin: '18px 0 8px' }}>
                {t('audit.detail.context')}
              </h3>
              <KvTable data={metadata!.context!} />
            </>
          )}

          {hasDiff && (
            <>
              <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-3)', margin: '18px 0 8px' }}>
                {t('audit.detail.changes')}
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: 'var(--text-3)' }}>{t('audit.detail.field')}</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: 'var(--text-3)' }}>{t('audit.detail.before')}</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: 'var(--text-3)' }}>{t('audit.detail.after')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(metadata!.diff!).map(([field, [before, after]]) => (
                    <tr key={field}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--line)', fontSize: 13 }}>
                        {t(`audit.fields.${field}`, { defaultValue: field })}
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--line)', fontSize: 13, color: 'var(--hot)', textDecoration: 'line-through', opacity: 0.8 }}>
                        {renderValue(before)}
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--line)', fontSize: 13, color: '#0E9F6E', fontWeight: 600 }}>
                        {renderValue(after)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px dashed var(--line)', color: 'var(--text-3)', fontSize: 12 }}>
            {t('audit.detail.technical')}: {entityTypeLabel} · <code style={{ background: 'var(--bg-soft)', padding: '1px 5px', borderRadius: 5 }}>{techLine}</code>
          </div>
        </div>
      </aside>
    </>
  );
}
